import { parseOptionalNumber } from '../../../lib/parseOptionalNumber';
import { serializeMadBuyLanguage } from './madBuyLanguageModel';
import { CampaignStatus, isCampaignStatus } from './model';
import {
  createTargetingFormValue,
  defaultTargetingFormValue,
  serializeTargetingFormValue,
} from '../../targeting/model';
import {
  createDefaultCustomTargetingExt,
  parseCustomTargetingExt,
  serializeCustomTargetingExt,
} from '../../targeting/customTargetingExtModel';
import { TargetingConsumer } from '../../targeting/config';
import { splitLines } from '../../targeting/modelHelpers';
import {
  MadBuyFrequencyCappingTimeUnit,
  MadBuyLineItemPacing,
  MadBuyLineItemType,
  type MadBuyLineItem,
  type MadBuyLineItemMutationPayload,
  type MadBuyLineItemTargeting,
  type MadBuyLineItemTargetingMutationPayload,
} from './lineItemsCoreModel';
import {
  formatDateTimeInputForPayload,
  formatMadBuyLineItemDateTimeInput,
  type LineItemDateTimeOptions,
} from './lineItemDateTimeModel';
import { convertMadBuyLineItemScheduleTimezone } from './lineItemScheduleTimezoneModel';
import {
  createMadBuyLineItemFormSchema,
  type MadBuyLineItemGeneralForm,
} from './lineItemFormSchema';

export type { MadBuyLineItemGeneralForm } from './lineItemFormSchema';

export const defaultMadBuyLineItemGeneralForm = {
  name: '',
  status: CampaignStatus.Active,
  adType: MadBuyLineItemType.Display,
  cpm: '',
  impressionsGoal: '',
  domain: '',
  margin: '0',
  upstreamCpmCapEnabled: false,
  upstreamCpmCapMinCpm: '',
  upstreamCpmCapMaxCpm: '',
  impressionTrackersEnabled: false,
  impressionTrackers: [''],
  frequencyCappingsEnabled: false,
  fcLimit: '0',
  fcDuration: '0',
  fcTimeUnit: MadBuyFrequencyCappingTimeUnit.Hour,
  startDate: '',
  endDate: '',
  scheduling: false,
  pacing: MadBuyLineItemPacing.Evenly,
  pacingPerHour: MadBuyLineItemPacing.Evenly,
  dayTimeActivities: [],
  targeting: defaultTargetingFormValue,
  customTargetingExt: createDefaultCustomTargetingExt(),
  lineItemCreatives: [],
  conversionPixelId: '',
  pendingZipCodesCsv: null,
  pendingIpAddressesCsv: null,
} satisfies MadBuyLineItemGeneralForm;

export function createMadBuyLineItemGeneralForm(
  lineItem: MadBuyLineItem | undefined,
  options: LineItemDateTimeOptions = {},
): MadBuyLineItemGeneralForm {
  if (!lineItem) {
    return {
      ...defaultMadBuyLineItemGeneralForm,
      targeting: structuredClone(defaultTargetingFormValue),
      customTargetingExt: createDefaultCustomTargetingExt(),
    };
  }

  const frequencyCapping = lineItem.frequencyCapping;
  const targeting = lineItem.targeting;

  return {
    name: lineItem.name,
    status: resolveFormStatus(lineItem.status),
    adType: resolveLineItemType(lineItem.adType),
    cpm: stringifyNumber(lineItem.cpm),
    impressionsGoal: stringifyNumber(lineItem.impressionsGoal),
    domain: lineItem.domain ?? '',
    margin: stringifyNumber(lineItem.margin ?? 0),
    upstreamCpmCapEnabled: Boolean(lineItem.upstreamCpmCapEnabled),
    upstreamCpmCapMinCpm: stringifyNumber(lineItem.upstreamCpmCapMinCpm),
    upstreamCpmCapMaxCpm: stringifyNumber(lineItem.upstreamCpmCapMaxCpm),
    impressionTrackersEnabled: Boolean(lineItem.impressionTrackers?.length),
    impressionTrackers: lineItem.impressionTrackers?.length
      ? [...lineItem.impressionTrackers]
      : [''],
    frequencyCappingsEnabled: Boolean(frequencyCapping?.fcActive),
    fcLimit: stringifyNumber(frequencyCapping?.fcLimit ?? 0),
    fcDuration: stringifyNumber(frequencyCapping?.fcDuration ?? 0),
    fcTimeUnit: frequencyCapping?.fcTimeUnit ?? MadBuyFrequencyCappingTimeUnit.Hour,
    startDate: formatMadBuyLineItemDateTimeInput(lineItem.startDate, options.timezone),
    endDate: formatMadBuyLineItemDateTimeInput(lineItem.endDate, options.timezone),
    scheduling: lineItem.scheduling ?? Boolean(lineItem.dayTimeActivities?.length),
    pacing: resolveLineItemPacing(lineItem.pacing),
    pacingPerHour: resolveLineItemPacing(lineItem.pacingPerHour),
    dayTimeActivities: convertMadBuyLineItemScheduleTimezone(
      lineItem.dayTimeActivities,
      'UTC',
      options.timezone,
      lineItem.startDate,
    ),
    targeting: createLineItemTargetingFormValue(targeting),
    customTargetingExt: parseCustomTargetingExt(targeting?.ext),
    lineItemCreatives: resolveLineItemCreatives(lineItem),
    conversionPixelId: lineItem.conversionPixelId ?? lineItem.conversionPixel?.id ?? '',
    pendingZipCodesCsv: null,
    pendingIpAddressesCsv: null,
  };
}

export function validateMadBuyLineItemGeneralForm(
  form: MadBuyLineItemGeneralForm,
  options: LineItemDateTimeOptions = {},
) {
  const errors: Partial<Record<keyof MadBuyLineItemGeneralForm, string>> = {};
  const result = createMadBuyLineItemFormSchema(options).safeParse(form);
  if (!result.success) {
    for (const issue of result.error.issues) {
      const field = issue.path[0];
      if (typeof field === 'string' && field in form) {
        const key = field as keyof MadBuyLineItemGeneralForm;
        errors[key] ??= issue.message;
      }
    }
  }

  return errors;
}

export function isMadBuyLineItemGeneralFormValid(
  form: MadBuyLineItemGeneralForm,
  options: LineItemDateTimeOptions = {},
) {
  return Object.keys(validateMadBuyLineItemGeneralForm(form, options)).length === 0;
}

export function calculateMadBuyLineItemBudget(
  form: Pick<MadBuyLineItemGeneralForm, 'cpm' | 'impressionsGoal'>,
) {
  const cpm = parseOptionalNumber(form.cpm) ?? 0;
  const impressionsGoal = parseOptionalNumber(form.impressionsGoal) ?? 0;

  return (cpm * impressionsGoal) / 1000;
}

export function serializeMadBuyLineItemGeneralForm(
  form: MadBuyLineItemGeneralForm,
  campaignId: string,
  options: LineItemDateTimeOptions = {},
): MadBuyLineItemMutationPayload {
  const cpm = parseOptionalNumber(form.cpm);
  const impressionsGoal = parseOptionalNumber(form.impressionsGoal);
  const margin = parseOptionalNumber(form.margin);
  const minCap = parseOptionalNumber(form.upstreamCpmCapMinCpm);
  const maxCap = parseOptionalNumber(form.upstreamCpmCapMaxCpm);

  if (
    !isMadBuyLineItemGeneralFormValid(form, options) ||
    cpm === undefined ||
    impressionsGoal === undefined
  ) {
    throw new Error('Cannot serialize invalid line item form');
  }

  return {
    name: form.name.trim(),
    status: form.status,
    adType: form.adType,
    cpm,
    impressionsGoal,
    campaign: { id: campaignId },
    domain: form.domain.trim(),
    margin: margin ?? null,
    upstreamCpmCapEnabled: form.upstreamCpmCapEnabled,
    upstreamCpmCapMinCpm: form.upstreamCpmCapEnabled ? (minCap ?? null) : null,
    upstreamCpmCapMaxCpm: form.upstreamCpmCapEnabled ? (maxCap ?? null) : null,
    impressionTrackers: form.impressionTrackersEnabled
      ? form.impressionTrackers.map((tracker) => tracker.trim()).filter(Boolean)
      : [],
    frequencyCapping: {
      fcActive: form.frequencyCappingsEnabled,
      fcDuration: parseOptionalNumber(form.fcDuration) ?? 0,
      fcLimit: parseOptionalNumber(form.fcLimit) ?? 0,
      fcTimeUnit: form.fcTimeUnit,
    },
    startDate: formatDateTimeInputForPayload(form.startDate, options.timezone),
    endDate: formatDateTimeInputForPayload(form.endDate, options.timezone),
    scheduling: form.scheduling,
    pacing: form.pacing,
    pacingPerHour: form.pacingPerHour,
    dayTimeActivities:
      form.scheduling && form.dayTimeActivities.length
        ? convertMadBuyLineItemScheduleTimezone(
            form.dayTimeActivities,
            options.timezone,
            'UTC',
            form.startDate,
          )
        : [],
    targeting: {
      ...serializeLineItemTargetingFormValue(form.targeting),
      ext: serializeCustomTargetingExt(form.customTargetingExt),
    },
    lineItemCreatives: form.lineItemCreatives,
    conversionPixelId: form.conversionPixelId || null,
  };
}

const legacyContextualTargetingKeys = [
  'includedChannels',
  'excludedChannels',
  'includedChannelGroups',
  'excludedChannelGroups',
  'includedContentTitles',
  'excludedContentTitles',
  'includedContentKeywords',
  'excludedContentKeywords',
  'includedContentGenres',
  'excludedContentGenres',
] as const;

function createLineItemTargetingFormValue(targeting: MadBuyLineItemTargeting | undefined) {
  const form = createTargetingFormValue(targeting, TargetingConsumer.LineItem);
  if (!targeting) return form;

  return {
    ...form,
    userLanguages: [...(targeting.userLanguages ?? [])],
    contextualChannelsIncluded:
      targeting.contentChannelsIncluded ?? form.contextualChannelsIncluded,
    contextualChannelsText:
      targeting.contentChannels === undefined
        ? form.contextualChannelsText
        : targeting.contentChannels.join('\n'),
    contextualChannelGroupsIncluded:
      targeting.contentChannelGroupsIncluded ?? form.contextualChannelGroupsIncluded,
    contextualChannelGroupsText:
      targeting.contentChannelGroups === undefined
        ? form.contextualChannelGroupsText
        : targeting.contentChannelGroups.join('\n'),
    contextualContentTitlesIncluded:
      targeting.contentTitlesIncluded ?? form.contextualContentTitlesIncluded,
    contextualContentTitlesText:
      targeting.contentTitles === undefined
        ? form.contextualContentTitlesText
        : targeting.contentTitles.join('\n'),
    contextualContentKeywordsIncluded:
      targeting.contentKeywordsIncluded ?? form.contextualContentKeywordsIncluded,
    contextualContentKeywordsText:
      targeting.contentKeywords === undefined
        ? form.contextualContentKeywordsText
        : targeting.contentKeywords.join('\n'),
    contextualContentGenresIncluded:
      targeting.contentGenresIncluded ?? form.contextualContentGenresIncluded,
    contextualContentGenresText:
      targeting.contentGenres === undefined
        ? form.contextualContentGenresText
        : targeting.contentGenres.join('\n'),
  };
}

// ZIP codes are replaced only through the CSV import. The MD-8048 line-item contract documents
// that an omitted (or null) ZIP/IP list in the PUT keeps the saved values, so ordinary saves omit it.
const omittedLineItemTargetingKeys = [...legacyContextualTargetingKeys, 'zipCodes'] as const;
const omittedLineItemTargetingKeySet: ReadonlySet<string> = new Set(omittedLineItemTargetingKeys);

function serializeLineItemTargetingFormValue(
  form: MadBuyLineItemGeneralForm['targeting'],
): MadBuyLineItemTargetingMutationPayload {
  const targeting = Object.fromEntries(
    Object.entries(serializeTargetingFormValue(form, TargetingConsumer.LineItem)).filter(
      ([key]) => !omittedLineItemTargetingKeySet.has(key),
    ),
  ) as Omit<
    ReturnType<typeof serializeTargetingFormValue>,
    (typeof omittedLineItemTargetingKeys)[number]
  >;

  return {
    ...targeting,
    userLanguages: form.userLanguages.map(serializeMadBuyLanguage),
    contentChannelsIncluded: form.contextualChannelsIncluded,
    contentChannels: splitLines(form.contextualChannelsText),
    contentChannelGroupsIncluded: form.contextualChannelGroupsIncluded,
    contentChannelGroups: splitLines(form.contextualChannelGroupsText),
    contentTitlesIncluded: form.contextualContentTitlesIncluded,
    contentTitles: splitLines(form.contextualContentTitlesText),
    contentKeywordsIncluded: form.contextualContentKeywordsIncluded,
    contentKeywords: splitLines(form.contextualContentKeywordsText),
    contentGenresIncluded: form.contextualContentGenresIncluded,
    contentGenres: splitLines(form.contextualContentGenresText),
  };
}

function resolveFormStatus(status: string | undefined): CampaignStatus {
  return isCampaignStatus(status) ? status : CampaignStatus.Active;
}

function resolveLineItemType(value: string | undefined): MadBuyLineItemType {
  return Object.values(MadBuyLineItemType).includes(value as MadBuyLineItemType)
    ? (value as MadBuyLineItemType)
    : MadBuyLineItemType.Display;
}

function resolveLineItemPacing(value: string | undefined): MadBuyLineItemPacing {
  return Object.values(MadBuyLineItemPacing).includes(value as MadBuyLineItemPacing)
    ? (value as MadBuyLineItemPacing)
    : MadBuyLineItemPacing.Evenly;
}

function resolveLineItemCreatives(lineItem: MadBuyLineItem) {
  if (lineItem.lineItemCreatives?.length) {
    return [...lineItem.lineItemCreatives];
  }
  if (lineItem.creatives?.length) {
    return [...lineItem.creatives];
  }

  return [];
}

function stringifyNumber(value: number | undefined) {
  return value === undefined || !Number.isFinite(value) ? '' : String(value);
}
